import {
  useHeaderFloorsQuery,
  useHeaderRoomQuery,
  useHeaderZoneQuery,
  useHeaderBuildingsQuery,
  useSidebarSiteQuery,
  SensorStatusFragment,
  DeviceStatus,
  type HeaderBuildingsQuery,
  type HeaderBuildingsQueryVariables,
  type HeaderFloorsQuery,
  type HeaderFloorsQueryVariables,
  type HeaderRoomQuery,
  type HeaderRoomQueryVariables,
  type HeaderZoneQuery,
  type HeaderZoneQueryVariables,
} from '@/.generated/graphql';
import { PageLayoutHeader } from '@/components/PageLayout';
import { useSRZId } from '@/hooks';
import { getSingleGraphQLModel } from '@/utils/getSingleGraphQLModel';
import { gql, type TypedDocumentNode } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { updateUrlParams } from '@/components/sidebar/utils';
import { UserMenu } from './UserMenu';
import { useMemo, useCallback } from 'react';
import { Icon } from '@/components/primitives/icon/Icon';
import { Breadcrumb, Button, Typography } from 'antd';
import TimeDisplay from '@/components/TimeDisplay';
import type { BreadcrumbProps } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { IconName } from '@/components/primitives/icon/generated/iconNames';
import { TagContainer } from '@/components/Tags/TagContainer';
import { useFeatureGate } from '@statsig/react-bindings';
import dayjs from 'dayjs';
import { useTimezone } from '@/hooks';
import { useDetailsPanel } from '@/contexts';
import { datadogRum } from '@datadog/browser-rum';

interface HeaderProps {}

// Hoisted to the top of the module so the `HEADER_QUERIES` template
// literals below can interpolate it. Used by `Floors` / `Room` / `Zone` to
// pull in the shared sensor-status fields.
const SENSOR_FRAGMENT = gql`
  fragment SensorStatus on Sensor {
    id
    name
    is_streaming
    device_status
  }
`;

// `TypedDocumentNode<TData, TVariables>` binds each query to its codegen
// data/variables types, so a `{ query: HEADER_QUERIES.Zone, variables: …}`
// object passed to a typed Apollo API rejects a `spaceId` variable at
// compile time. `HEADER_QUERIES` is module-private; the map is attached
// to the exported `Header` via `Object.assign` below so external
// consumers reach the documents as `Header.queries.X`. The component
// body inside this module references `HEADER_QUERIES.X` directly to
// avoid a forward-reference cycle on `Header` (defined after `HeaderImpl`).
const HEADER_QUERIES = {
  Buildings: gql`
    query HeaderBuildings($buildingId: ID!) {
      building(id: $buildingId) {
        id
        name
      }
    }
  ` as TypedDocumentNode<HeaderBuildingsQuery, HeaderBuildingsQueryVariables>,
  Floors: gql`
    query HeaderFloors($spaceId: String!) {
      floors(ids: [$spaceId]) {
        data {
          id: floor_id
          name
          tags {
            id
            name
          }
          sensors {
            ...SensorStatus
          }
          hives {
            serial_number
            config {
              frame_rate {
                monday
                tuesday
                wednesday
                thursday
                friday
                saturday
                sunday
              }
            }
          }
          ...UserMenuFloor
        }
      }
    }
    ${SENSOR_FRAGMENT}
    ${UserMenu.fragments.Floor}
  ` as TypedDocumentNode<HeaderFloorsQuery, HeaderFloorsQueryVariables>,
  Room: gql`
    query HeaderRoom($roomId: String!) {
      rooms(ids: [$roomId]) {
        data {
          id: room_id
          name
          tags {
            id
            name
          }
          sensors {
            ...SensorStatus
          }
        }
      }
    }
    ${SENSOR_FRAGMENT}
  ` as TypedDocumentNode<HeaderRoomQuery, HeaderRoomQueryVariables>,
  // can only get tags on zones, not rooms or spaces yet
  Zone: gql`
    query HeaderZone($zoneId: String!) {
      zones(ids: [$zoneId]) {
        data {
          id: zone_id
          name
          tags {
            id
            name
          }
          sensors {
            ...SensorStatus
          }
        }
      }
    }
    ${SENSOR_FRAGMENT}
  ` as TypedDocumentNode<HeaderZoneQuery, HeaderZoneQueryVariables>,
};

const HeaderImpl: React.FC<HeaderProps> = () => {
  const { isOpen, close, open } = useDetailsPanel();
  const url = useLocation();
  const navigate = useNavigate();
  const { roomId, spaceId, zoneId, buildingId, siteId } = useSRZId();
  const { data: spaceData, loading: spaceLoading } = useHeaderFloorsQuery({
    // skip validates this assertion.

    variables: { spaceId: spaceId! },
    skip: !spaceId,
  });

  const { data: buildingData } = useHeaderBuildingsQuery({
    // skip validates this assertion.

    variables: { buildingId: buildingId! },
    skip: !buildingId,
  });

  // Sites have no dedicated single-id query — read from the sidebar query
  // (Apollo cache is already populated from `<SidebarMenu>`'s usage). Used
  // both for the selected-site name and to walk the hierarchy when
  // computing the ancestor breadcrumb above the title.
  const { data: sidebarSitesData } = useSidebarSiteQuery({
    fetchPolicy: 'cache-first',
  });
  const selectedSite = useMemo(
    () => (siteId ? sidebarSitesData?.sites?.data?.find((s) => s.id === siteId) : undefined),
    [siteId, sidebarSitesData],
  );

  // Ancestor path above the title. Walks the sidebar's site hierarchy
  // to find the current selection, then renders a Breadcrumb where each
  // ancestor is a clickable dropdown of its siblings — pick another
  // Site / Building / Floor to jump straight to its first floor without
  // returning to the sidebar. The current item is NOT included (the
  // title below already shows it).
  // Read only the array we walk; using `sidebarSitesData` itself as the dep
  // would invalidate the memo on every Apollo cache write that touches any
  // field on this query (sensor stats, floor metadata, etc.) and force a
  // full re-walk + JSX rebuild even when the structure didn't change.
  const sidebarSites = sidebarSitesData?.sites?.data;
  const breadcrumbItems = useMemo(() => {
    const sites = sidebarSites ?? [];
    const isDevicesPage = url.pathname === '/devices' || url.pathname.startsWith('/devices/');

    // "All Spaces" is the implicit root above every site. It's clickable
    // in Devices (navigates to the unscoped tables view) and a plain
    // label in Monitor / Studio (those modules don't support an
    // unscoped state — clicking would only confuse).
    const allSpacesItem = isDevicesPage
      ? { title: 'All Spaces', onClick: () => updateUrlParams(navigate, {}) }
      : { title: 'All Spaces' };

    // Each dropdown navigates to the picked sibling's first floor —
    // a reasonable "show me the data here" landing for cross-module
    // browsing without forcing the user into a deeper choice.
    const firstFloorIdInSite = (site: (typeof sites)[number]): string | undefined =>
      site.buildings?.[0]?.floors?.[0]?.id;
    const firstFloorIdInBuilding = (
      b: NonNullable<(typeof sites)[number]['buildings']>[number],
    ): string | undefined => b.floors?.[0]?.id;

    // Icons match the sidebar tree (site → building → floor) so the
    // breadcrumb reads as a compact reflection of the sidebar path.
    // `verticalAlign: 'middle'` on the wrapper makes the inline-flex
    // span sit on the line's vertical centre — antd renders its
    // dropdown chevron as a separate inline sibling that already aligns
    // to the centre, so without this we'd get a baseline mismatch
    // (chevron drifting visually below the icon + text).
    const titleWithIcon = (iconName: IconName, name: string) => (
      <span className="inline-flex items-center gap-1" style={{ verticalAlign: 'middle' }}>
        <Icon name={iconName} size={14} />
        {name}
      </span>
    );
    const labelWithIcon = (iconName: IconName, name: string) => (
      <span className="inline-flex items-center gap-2" style={{ verticalAlign: 'middle' }}>
        <Icon name={iconName} size={14} />
        {name}
      </span>
    );

    // Cap dropdown height so big spaces (e.g. a floor with 30+ rooms)
    // don't render an overflowing menu — anything past the cap scrolls.
    const SIBLINGS_MAX_HEIGHT = 320;
    const siblingsMenu = (
      items: NonNullable<NonNullable<BreadcrumbProps['items']>[number]['menu']>['items'],
    ) => ({
      items,
      style: { maxHeight: SIBLINGS_MAX_HEIGHT, overflowY: 'auto' as const },
    });

    // Build the breadcrumb top-down: always start with All Spaces, then
    // append each level matched in the URL — INCLUDING the current leaf
    // (so "All Spaces" stays visible on Devices' unscoped page, and
    // every other page shows its full path: All Spaces / Site / …).
    //
    // Each level's dropdown lists *siblings* — items at the same level
    // excluding the one already on screen. Including the current item
    // would render a chevron that "navigates" to where you already are.
    // We also drop the dropdown entirely when there are no siblings, so
    // a single-option chevron isn't presented as a choice.
    const items: NonNullable<BreadcrumbProps['items']> = [allSpacesItem];

    // A floor counts as the path target if EITHER the URL points at it
    // directly (`spaceId`) OR a deep link points at one of its rooms /
    // zones without `spaceId`. The room/zone branches let breadcrumbs
    // render correctly for `?roomId=…` / `?zoneId=…` URLs that originate
    // outside the sidebar (deep links, shared URLs).
    const floorMatches = (f: {
      id: string;
      rooms?: { id: string }[] | null;
      zones?: { id: string }[] | null;
    }) =>
      Boolean(
        (spaceId && f.id === spaceId) ||
          (roomId && (f.rooms ?? []).some((r) => r.id === roomId)) ||
          (zoneId && (f.zones ?? []).some((z) => z.id === zoneId)),
      );

    for (const site of sites) {
      const matchesSite = siteId && site.id === siteId;
      const matchesBuildingInSite = (site.buildings ?? []).some(
        (b) => (buildingId && b.id === buildingId) || (b.floors ?? []).some(floorMatches),
      );
      if (!matchesSite && !matchesBuildingInSite) continue;

      const siteSiblings = sites.filter((s) => s.id !== site.id);
      const siteSiblingsMenu =
        siteSiblings.length > 0
          ? siblingsMenu(
              siteSiblings.map((s) => ({
                key: s.id,
                label: labelWithIcon('icon-siteoutlined', s.name),
                onClick: () => {
                  const floorId = firstFloorIdInSite(s);
                  if (floorId) updateUrlParams(navigate, { floorId });
                },
              })),
            )
          : undefined;

      items.push({
        title: titleWithIcon('icon-siteoutlined', site.name),
        ...(siteSiblingsMenu && { menu: siteSiblingsMenu }),
      });
      if (matchesSite) return items;

      const buildings = site.buildings ?? [];
      for (const b of buildings) {
        const matchesBuilding = buildingId && b.id === buildingId;
        const matchesFloorInBuilding = (b.floors ?? []).some(floorMatches);
        if (!matchesBuilding && !matchesFloorInBuilding) continue;

        const buildingSiblings = buildings.filter((b2) => b2.id !== b.id);
        const buildingSiblingsMenu =
          buildingSiblings.length > 0
            ? siblingsMenu(
                buildingSiblings.map((b2) => ({
                  key: b2.id,
                  label: labelWithIcon('building', b2.name),
                  onClick: () => {
                    const floorId = firstFloorIdInBuilding(b2);
                    if (floorId) updateUrlParams(navigate, { floorId });
                  },
                })),
              )
            : undefined;

        items.push({
          title: titleWithIcon('building', b.name),
          ...(buildingSiblingsMenu && { menu: buildingSiblingsMenu }),
        });
        if (matchesBuilding) return items;

        const floors = b.floors ?? [];
        for (const f of floors) {
          if (floorMatches(f)) {
            const floorSiblings = floors.filter((f2) => f2.id !== f.id);
            const floorSiblingsMenu =
              floorSiblings.length > 0
                ? siblingsMenu(
                    floorSiblings.map((f2) => ({
                      key: f2.id,
                      label: labelWithIcon('layer', f2.name),
                      onClick: () => updateUrlParams(navigate, { floorId: f2.id }),
                    })),
                  )
                : undefined;

            items.push({
              title: titleWithIcon('layer', f.name),
              ...(floorSiblingsMenu && { menu: floorSiblingsMenu }),
            });
            // If a Room or Zone is also selected, append it as the leaf
            // breadcrumb item so the path matches the sidebar's deepest
            // selectable level (Monitor: rooms / zones).
            if (roomId) {
              const rooms = f.rooms ?? [];
              const room = rooms.find((r) => r.id === roomId);
              if (room) {
                const roomSiblings = rooms.filter((r) => r.id !== roomId);
                const roomSiblingsMenu =
                  roomSiblings.length > 0
                    ? siblingsMenu(
                        roomSiblings.map((r) => ({
                          key: r.id,
                          label: labelWithIcon('room', r.name),
                          onClick: () => updateUrlParams(navigate, { floorId: f.id, roomId: r.id }),
                        })),
                      )
                    : undefined;
                items.push({
                  title: titleWithIcon('room', room.name),
                  ...(roomSiblingsMenu && { menu: roomSiblingsMenu }),
                });
              }
            } else if (zoneId) {
              const zones = f.zones ?? [];
              const zone = zones.find((z) => z.id === zoneId);
              if (zone) {
                const zoneSiblings = zones.filter((z) => z.id !== zoneId);
                const zoneSiblingsMenu =
                  zoneSiblings.length > 0
                    ? siblingsMenu(
                        zoneSiblings.map((z) => ({
                          key: z.id,
                          label: labelWithIcon('zone', z.name),
                          onClick: () => updateUrlParams(navigate, { floorId: f.id, zoneId: z.id }),
                        })),
                      )
                    : undefined;
                items.push({
                  title: titleWithIcon('zone', zone.name),
                  ...(zoneSiblingsMenu && { menu: zoneSiblingsMenu }),
                });
              }
            }
            return items;
          }
        }
      }
    }
    return items;
  }, [sidebarSites, siteId, buildingId, spaceId, roomId, zoneId, navigate, url.pathname]);

  const space = getSingleGraphQLModel(spaceData?.floors);
  const skip = !spaceId || !roomId;
  const { data: roomData, loading: roomLoading } = useHeaderRoomQuery({
    // asserted by skip below

    variables: { roomId: roomId! },
    skip,
  });
  const { data: zoneData, loading: zoneLoading } = useHeaderZoneQuery({
    // asserted by skip below

    variables: { zoneId: zoneId! },
    skip: !zoneId,
  });

  const { value: tagging } = useFeatureGate('tagging');

  const { room, zone, associatedTags } = useMemo(() => {
    const room = roomData?.rooms?.data?.[0];
    const zone = zoneData?.zones?.data?.[0];
    const associatedTags =
      roomId || zoneId
        ? roomId
          ? roomData?.rooms?.data?.[0]?.tags
          : zoneData?.zones?.data?.[0]?.tags
        : spaceData?.floors?.data?.[0]?.tags;

    return { room, zone, associatedTags };
  }, [roomData, zoneData, spaceData, roomId, zoneId]);

  // Devices' "All Spaces" pseudo-selection: on /devices with no space params,
  // the sidebar's All Spaces row is the active selection — render the header
  // as a plain "All Spaces" label with no icon.
  const isDevicesAllSpaces =
    url.pathname === '/devices' && !spaceId && !roomId && !zoneId && !buildingId && !siteId;

  const { headerName, iconName } = useMemo(() => {
    if (spaceLoading || roomLoading || zoneLoading) {
      return { headerName: '', iconName: 'layer' as IconName };
    }

    const headerName = buildingData?.building?.name
      ? buildingData.building.name
      : url.pathname === '/studio'
      ? space?.name
      : roomId && room
      ? room.name
      : zoneId && zone
      ? zone.name
      : spaceId && space
      ? space.name
      : selectedSite
      ? selectedSite.name
      : isDevicesAllSpaces
      ? 'All Spaces'
      : '';

    const iconName: IconName =
      url.pathname === '/studio'
        ? 'layer'
        : roomId && room
        ? 'room'
        : zoneId && zone
        ? 'zone'
        : spaceId && space
        ? 'layer'
        : buildingId
        ? 'building'
        : selectedSite
        ? 'icon-siteoutlined'
        : 'layer';

    return { headerName: headerName || '', iconName };
  }, [
    spaceLoading,
    roomLoading,
    zoneLoading,
    buildingData?.building?.name,
    url.pathname,
    space?.name,
    roomId,
    room?.name,
    zoneId,
    zone?.name,
    spaceId,
    buildingId,
    selectedSite,
    isDevicesAllSpaces,
  ]);
  const associateId = zoneId || roomId || spaceId;
  const isLoading = spaceLoading || roomLoading || zoneLoading;

  const queryToRefetch = useMemo(() => {
    const queries = [];
    if (zoneId) {
      queries.push({
        query: HEADER_QUERIES.Zone,
        variables: { zoneId },
      });
    } else if (roomId) {
      queries.push({
        query: HEADER_QUERIES.Room,
        variables: { roomId },
      });
    } else if (spaceId) {
      queries.push({
        query: HEADER_QUERIES.Floors,
        variables: { spaceId },
      });
    }
    return queries;
  }, [zoneId, roomId, spaceId]);

  const sensors = roomId ? room?.sensors : zoneId ? zone?.sensors : space?.sensors;
  const tz = useTimezone();
  const hiveConfig = space?.hives?.[0]?.config?.frame_rate;
  // `nowTime` is computed inside the memo (not as a prop dep) so we don't
  // recompute it on every render — `getStatus` only needs the current
  // hour, and the memo correctly invalidates whenever sensors / hiveConfig
  // / loading state shifts. The status is a best-effort badge; staleness
  // by a minute boundary until the next data tick is acceptable.
  const sensorStatus = useMemo(() => {
    if (spaceLoading || roomLoading || zoneLoading) return null;
    const nowTime = dayjs().tz(tz).format('HH:mm');
    return getStatus(nowTime, tz, sensors ?? [], hiveConfig);
  }, [tz, sensors, hiveConfig, spaceLoading, roomLoading, zoneLoading]);

  const handleToggle = useCallback(() => {
    datadogRum.addAction('Toggle Details Panel/Settings', {
      source: 'settings-and-details',
      action: isOpen ? 'close' : 'open',
    });
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  return (
    <PageLayoutHeader className="z-[9] bg-surface-sunken pt-3">
      {/* `PageLayoutHeader` itself is `flex flex-row` — wrap our two
       * stacked rows (breadcrumb above the title) in a `flex-col` so
       * they sit on top of each other instead of side by side. */}
      <div className="flex w-full flex-col">
        {breadcrumbItems.length > 0 && (
          // Breadcrumb on the left, Local Time pinned to the right — same
          // row so the header doesn't waste vertical space on a separate
          // time-only line (previously the time lived in the Tabs strip).
          <div className="flex items-center justify-between gap-3" style={{ padding: '4px 0' }}>
            <Breadcrumb
              // Full path including the current page. Site / Building /
              // Floor each open a dropdown of siblings for lateral jumps.
              // Default 14px text aligns with the 14px icons — no fontSize
              // override.
              items={breadcrumbItems}
            />
            <Typography.Text style={{ whiteSpace: 'nowrap' }}>
              <Typography.Text type="secondary" style={{ marginRight: 4 }}>
                Local Time:
              </Typography.Text>
              <TimeDisplay />
            </Typography.Text>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-4 py-1 justify-between">
          {headerName && (
            <div className="flex items-center truncate" data-id="header-name-icon">
              {/* No icon for the Devices "All Spaces" pseudo-selection — it's a
                catch-all label, not a specific space, so an icon would feel
                arbitrary. */}
              {!isDevicesAllSpaces && <Icon name={iconName} className="mr-2 h-7 w-7" />}
              <Typography.Title level={4} style={{ margin: 0 }}>
                {headerName}
              </Typography.Title>
              {url.pathname !== '/devices' &&
                url.pathname !== '/studio' &&
                !isLoading &&
                tagging &&
                !buildingId && (
                  <div className="ml-4">
                    <TagContainer
                      associatedTags={associatedTags?.map((tag) => tag.name) ?? []}
                      itemId={associateId}
                      queryToRefetch={queryToRefetch}
                    />
                  </div>
                )}
            </div>
          )}
          <div className="flex items-center gap-2" style={{ minHeight: 32 }}>
            {/* Devices "All Spaces" pseudo-selection has nothing to show on the
              right (no sensor status, no per-space settings). The empty
              `minHeight: 32` container above keeps the header at the same
              height as the other selection pages. */}
            {!isDevicesAllSpaces && !buildingId && !siteId && sensorStatus !== null && (
              <span className="text-content-secondary text-sm font-normal" data-id="sensor-status">
                <span
                  className={
                    sensorStatus === 'ACTIVE'
                      ? 'text-content-primary'
                      : sensorStatus === 'INACTIVE'
                      ? 'text-status-error'
                      : 'text-status-asleep'
                  }
                >
                  Sensor{' '}
                  {sensorStatus === SensorStatus.ACTIVE
                    ? 'Active'
                    : sensorStatus === SensorStatus.INACTIVE
                    ? 'Inactive'
                    : 'Asleep'}
                </span>
                <span className="ml-1">in this {roomId ? 'room' : zoneId ? 'zone' : 'space'}</span>
              </span>
            )}
            {/* Settings toggle visible only at Floor / Room / Zone level —
              hidden for the All Spaces pseudo-selection, Site, and Building
              because none of those levels have configurable settings today
              (the gear would open an empty panel). Row height stays
              consistent via the parent div's `minHeight: 32`. */}
            {!isDevicesAllSpaces && !buildingId && !siteId && (
              <Button
                data-id="showHideDetails"
                type="text"
                icon={<SettingOutlined />}
                onClick={handleToggle}
              />
            )}
          </div>
        </div>
      </div>
    </PageLayoutHeader>
  );
};

// Attach `HEADER_QUERIES` as a static `queries` property on the exported
// `Header` so external consumers reach the documents the same way the
// SuperUserPage Header does (`Header.queries.X`). `Object.assign` lets
// TypeScript infer the intersection type without a cast — no `as unknown
// as` needed, and no risk of the asserted shape diverging from reality.
export const Header = Object.assign(HeaderImpl, { queries: HEADER_QUERIES });

export enum SensorStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ASLEEP = 'ASLEEP',
}
/**
 * Determines the operational status of sensors based on frame rate configuration
 * @param nowTime Current time in 24-hour format (HH:mm)
 * @param sensors Array of sensors to check
 * @param hiveConfig Hive frame rate configuration with day-specific operation hours
 * @returns Status: "ACTIVE", "INACTIVE", or "ASLEEP"
 */

export function getStatus(
  nowTime: string,
  tz: string,
  sensors: SensorStatusFragment[] | undefined,
  hiveConfig?: {
    monday?: string;
    tuesday?: string;
    wednesday?: string;
    thursday?: string;
    friday?: string;
    saturday?: string;
    sunday?: string;
  },
): SensorStatus {
  const activeSensors = sensors?.filter((sensor) => sensor.device_status !== DeviceStatus.Archived);
  if (!activeSensors?.length || !hiveConfig) {
    return SensorStatus.INACTIVE;
  }
  const allSensorsOffline = activeSensors.every((sensor) => sensor.is_streaming === false);
  if (allSensorsOffline) {
    return SensorStatus.INACTIVE;
  }
  const now = dayjs().tz(tz);
  const currentDay = now.format('dddd').toLowerCase();
  const hours = hiveConfig[currentDay as keyof typeof hiveConfig];

  if (hours) {
    const [startHour, endHour] = hours.split(',');
    const startTime = parseInt(startHour);
    const endTime = parseInt(endHour);
    const currentHour = parseInt(nowTime.split(':')[0]);

    const isWithinOperatingHours = currentHour >= startTime && currentHour < endTime;

    if (!isWithinOperatingHours) {
      return SensorStatus.ASLEEP;
    }
  }

  return SensorStatus.ACTIVE;
}
